import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupsAPI } from '../services/api';
import { Group } from '../types';
import { AppLayout } from '../components/layout/AppLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const data = await groupsAPI.getAll();
      setGroups(data.groups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGroupEmoji = (name: string): string => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('trip') || lowerName.includes('vacation')) return '✈️';
    if (lowerName.includes('dinner') || lowerName.includes('lunch') || lowerName.includes('food')) return '🍽️';
    if (lowerName.includes('party')) return '🎉';
    if (lowerName.includes('office') || lowerName.includes('work')) return '💼';
    if (lowerName.includes('home') || lowerName.includes('flat') || lowerName.includes('roommate')) return '🏠';
    return '👥';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <Header
        title="Groups"
        rightAction={
          <button
            onClick={() => navigate('/groups/new')}
            className="p-2 rounded-xl hover:bg-slate-100"
          >
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        }
      />

      <main className="p-4 pb-24 space-y-4">
        {groups.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No groups yet</h3>
              <p className="text-slate-500 mb-6 max-w-xs mx-auto">
                Create a group to start splitting bills with friends and family
              </p>
              <Button onClick={() => navigate('/groups/new')}>
                Create Your First Group
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => (
              <Card
                key={group.id}
                hover
                onClick={() => navigate(`/groups/${group.id}`)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0">
                    {getGroupEmoji(group.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{group.name}</h3>
                    {group.description && (
                      <p className="text-sm text-slate-500 truncate">{group.description}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {group.member_count || 0} members
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </AppLayout>
  );
};

export default Groups;
